import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Loader2 } from 'lucide-react';
import { adminApi, authApi, userApi } from '../services/api';
import { User } from '../types';
import Alert from '../components/Alert';
import DataTable from '../components/DataTable';

const AdminUsersList: React.FC = () => {
    const [searchParams] = useSearchParams();
    const accountIdParam = searchParams.get('accountId');

    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [togglingUserId, setTogglingUserId] = useState<string | null>(null);
    const [togglingTwoFactorUserId, setTogglingTwoFactorUserId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const itemsPerPage = 10;

    useEffect(() => {
        fetchUsers();
    }, [accountIdParam]);

    useEffect(() => {
        const filtered = users.filter(user =>
            user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.account.number.toString().includes(searchTerm)
        );
        setFilteredUsers(filtered);
        setCurrentPage(1);
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            let response;
            if (accountIdParam) {
                response = await userApi.getByAccountId(accountIdParam);
            } else {
                response = await userApi.getAll();
            }
            setUsers(response.data || []);
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Não foi possível carregar os usuários.';
            setError(friendly);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const isUserActive = (user: User): boolean => {
        if (typeof user.isActive === 'boolean') return user.isActive;
        if (typeof user.active === 'boolean') return user.active;
        return true;
    };

    const isUserTwoFactorEnabled = (user: User): boolean => {
        if (typeof user.twoFactorEnabled === 'boolean') return user.twoFactorEnabled;
        if (typeof user.mfaEnabled === 'boolean') return user.mfaEnabled;
        return false;
    };

    const handleToggleUserStatus = async (user: User) => {
        const nextIsActive = !isUserActive(user);
        setTogglingUserId(user.id);
        setError(null);

        try {
            const response = await adminApi.updateUser(user.id);
            const updatedUser = response.data;

            setUsers((currentUsers) =>
                currentUsers.map((currentUser) =>
                    currentUser.id === user.id
                        ? {
                            ...currentUser,
                            ...updatedUser,
                            isActive: typeof updatedUser?.isActive === 'boolean' ? updatedUser.isActive : nextIsActive,
                            active: typeof updatedUser?.active === 'boolean' ? updatedUser.active : nextIsActive,
                        }
                        : currentUser
                )
            );
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao atualizar o status do usuário.';
            setError(friendly);
        } finally {
            setTogglingUserId(null);
        }
    };

    const handleDisableUserTwoFactor = async (user: User) => {
        if (!isUserTwoFactorEnabled(user)) {
            return;
        }

        setTogglingTwoFactorUserId(user.id);
        setError(null);

        try {
            await authApi.disableUserTwoFactor(user.username);

            setUsers((currentUsers) =>
                currentUsers.map((currentUser) =>
                    currentUser.id === user.id
                        ? {
                            ...currentUser,
                            twoFactorEnabled: false,
                            mfaEnabled: false,
                        }
                        : currentUser
                )
            );
        } catch (e: any) {
            const friendly = e?.response?.data?.translatedMessage || 'Falha ao atualizar o MFA do usuário.';
            setError(friendly);
        } finally {
            setTogglingTwoFactorUserId(null);
        }
    };

    const isAdminUser = (user: User): boolean => user.username === 'admin';

    const columns = [
        {
            key: 'name',
            label: 'Nome',
        },
        {
            key: 'username',
            label: 'Usuário',
            render: (value: string) => <span className="font-mono text-sm">@{value}</span>,
        },
        {
            key: 'account',
            label: 'Conta',
            render: (value: User['account']) => (
                <div>
                    <div className="font-medium">{value.name}</div>
                    <div className="text-xs text-secondary-600">#{value.number}</div>
                </div>
            ),
        },
        {
            key: 'createdAt',
            label: 'Criado em',
            render: (value: string) => <span>{new Date(value).toLocaleString('pt-BR')}</span>,
        },
        {
            key: 'isActive',
            label: 'Status',
            render: (_value: boolean | undefined, user: User) => {
                const active = isUserActive(user);
                return (
                    <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                            active
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-secondary-200 text-secondary-700'
                        }`}
                    >
                        {active ? 'Ativo' : 'Inativo'}
                    </span>
                );
            },
        },
        {
            key: 'twoFactorEnabled',
            label: 'MFA',
            render: (_value: boolean | undefined, user: User) => {
                const enabled = isUserTwoFactorEnabled(user);
                return (
                    <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                            enabled
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-secondary-200 text-secondary-700'
                        }`}
                    >
                        {enabled ? 'Ativo' : 'Inativo'}
                    </span>
                );
            },
        },
    ];

    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedData = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

    const filteredAccount = useMemo(() => {
        if (!accountIdParam || users.length === 0) return null;
        return users[0]?.account || null;
    }, [accountIdParam, users]);

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-3">
                <div className="p-3 bg-primary-100 rounded-lg">
                    <Users className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold text-secondary-900">Usuários</h1>
                    <p className="text-secondary-600">
                        {filteredAccount
                            ? `Usuários da conta: ${filteredAccount.name} (#${filteredAccount.number})`
                            : 'Gerencie os usuários do sistema'
                        }
                    </p>
                </div>
            </div>

            {error && <Alert type="error" title="Erro ao carregar" message={error} />}

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
                </div>
            ) : users.length === 0 ? (
                <div className="bg-white p-8 rounded-lg border border-secondary-200 text-center">
                    <Users className="w-12 h-12 text-secondary-400 mx-auto mb-4" />
                    <p className="text-secondary-600">
                        {accountIdParam ? 'Nenhum usuário encontrado para esta conta.' : 'Nenhum usuário cadastrado ainda.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-secondary-200">
                    <DataTable
                        data={paginatedData}
                        columns={columns}
                        searchPlaceholder="Buscar por nome, usuário ou conta..."
                        searchValue={searchTerm}
                        onSearchChange={setSearchTerm}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        actionsRenderer={(user: User) => {
                            if (isAdminUser(user)) {
                                return <span className="text-xs text-secondary-500">Administrador</span>;
                            }

                            const active = isUserActive(user);
                            const twoFactorEnabled = isUserTwoFactorEnabled(user);
                            const isSubmittingStatus = togglingUserId === user.id;
                            const isSubmittingTwoFactor = togglingTwoFactorUserId === user.id;

                            return (
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-secondary-600">Usuário</span>
                                        <button
                                            type="button"
                                            role="switch"
                                            aria-checked={active}
                                            aria-label={`${active ? 'Desativar' : 'Ativar'} usuário ${user.name}`}
                                            onClick={() => handleToggleUserStatus(user)}
                                            disabled={isSubmittingStatus}
                                            className={`inline-flex h-7 w-12 items-center rounded-full border transition-colors ${
                                                active
                                                    ? 'border-emerald-500 bg-emerald-500'
                                                    : 'border-secondary-300 bg-secondary-300'
                                            } ${isSubmittingStatus ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                        >
                                            <span
                                                className={`inline-flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm transition-transform ${
                                                    active ? 'translate-x-6' : 'translate-x-1'
                                                }`}
                                            >
                                                {isSubmittingStatus && <Loader2 className="h-3 w-3 animate-spin text-secondary-500" />}
                                            </span>
                                        </button>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-secondary-600">MFA</span>
                                        {twoFactorEnabled ? (
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={twoFactorEnabled}
                                                aria-label={`Desativar MFA do usuário ${user.name}`}
                                                onClick={() => handleDisableUserTwoFactor(user)}
                                                disabled={isSubmittingTwoFactor}
                                                className={`inline-flex h-7 w-12 items-center rounded-full border border-sky-500 bg-sky-500 transition-colors ${
                                                    isSubmittingTwoFactor ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                                                }`}
                                            >
                                                <span
                                                    className="inline-flex h-5 w-5 translate-x-6 items-center justify-center rounded-full bg-white shadow-sm transition-transform"
                                                >
                                                    {isSubmittingTwoFactor && <Loader2 className="h-3 w-3 animate-spin text-secondary-500" />}
                                                </span>
                                            </button>
                                        ) : (
                                            <span className="text-xs text-secondary-500">Somente desativar</span>
                                        )}
                                    </div>
                                </div>
                            );
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default AdminUsersList;
